/**
 * SAP's "List of India Chapter ID" window, for picking an item's HSN code.
 *
 * SAP keeps the codes as chapter rows of Chapter, Heading and Subheading, and a
 * document or item points at one of them. Both the item master and a purchase order
 * line offer the same window, so it lives here rather than in either of them.
 */
import React, { useEffect, useState } from 'react';
import api from '../api/api';

/** The list itself, loaded once per mount. */
export function useHsnCodes() {
  const [codes, setCodes] = useState([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/items/hsn-codes?limit=500')
      .then((rows) => { if (!cancelled) setCodes(Array.isArray(rows) ? rows : rows?.rows || []); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, []);

  return { codes, loadError };
}

/**
 * The small ruled button that opens the window, sized to sit inside a field.
 * Three lines, the way SAP marks a field you choose from a list.
 */
export function HsnListButton({ onClick, title = 'List of India Chapter ID', className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      tabIndex={-1}
      className={`shrink-0 flex flex-col items-center justify-center gap-[2px] ${className}`}
    >
      <span className="block w-[10px] h-[1.5px] bg-sap-textLight" />
      <span className="block w-[10px] h-[1.5px] bg-sap-textLight" />
      <span className="block w-[10px] h-[1.5px] bg-sap-textLight" />
    </button>
  );
}

export default function HsnChooser({ codes, current, onPick, onClose }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const shown = needle
    ? codes.filter((c) => {
      const code = String(c.hsn_code || '').toLowerCase();
      const desc = String(c.description || '').toLowerCase();
      const joined = `${c.chapter || ''}${c.heading || ''}${c.subheading || ''}`;
      return code.includes(needle) || desc.includes(needle) || (digits && joined.includes(digits));
    })
    : codes;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl border border-sap-border w-[720px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <h3 className="text-sm font-bold text-sap-text">List of India Chapter ID</h3>
          <button type="button" onClick={onClose}
            className="text-sap-textLight hover:text-sap-text text-base leading-none">&#10005;</button>
        </div>

        <div className="px-4 py-2.5 border-b">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find by code, chapter or description - e.g. 8515 or Raw Material"
            className="w-full h-8 px-3 text-xs border border-sap-border rounded
              focus:outline-none focus:border-sap-blue"
          />
          <p className="mt-1 text-[10px] text-sap-textLight">
            {shown.length} of {codes.length} code{codes.length === 1 ? '' : 's'}. Click a row to use it.
          </p>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-sap-bg border-b border-sap-border">
              <tr className="text-sap-textLight">
                <th className="text-left px-3 py-1.5 font-semibold w-12">#</th>
                <th className="text-left px-3 py-1.5 font-semibold">Description</th>
                <th className="text-left px-3 py-1.5 font-semibold w-20">Chapter</th>
                <th className="text-left px-3 py-1.5 font-semibold w-20">Heading</th>
                <th className="text-left px-3 py-1.5 font-semibold w-24">Subheading</th>
                <th className="text-left px-3 py-1.5 font-semibold w-28">HSN</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-sap-textLight italic">
                    Nothing matches that search.
                  </td>
                </tr>
              ) : shown.map((c, i) => (
                <tr
                  key={c.abs_entry}
                  onClick={() => onPick(c.hsn_code)}
                  className={`border-t border-sap-border/30 cursor-pointer hover:bg-sap-blueLight/60
                    ${String(c.hsn_code) === String(current) ? 'bg-sap-blueLight' : ''}`}
                >
                  <td className="px-3 py-1.5 text-sap-textLight">{i + 1}</td>
                  <td className="px-3 py-1.5 text-sap-text">{c.description || ''}</td>
                  <td className="px-3 py-1.5">{c.chapter || ''}</td>
                  <td className="px-3 py-1.5">{c.heading || ''}</td>
                  <td className="px-3 py-1.5">{c.subheading || ''}</td>
                  <td className="px-3 py-1.5 font-medium text-sap-blue">{c.hsn_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 px-4 py-2.5 border-t">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-sap-textLight border border-sap-border rounded hover:bg-gray-100">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
